import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, LogOut, RefreshCw, Eye, Mail, ArrowLeft, Loader2, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "beintec-admin-token";

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [messages, setMessages] = useState(null);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setStats(null);
    setMessages(null);
  }, []);

  const load = useCallback(
    async (tk) => {
      try {
        const [s, m] = await Promise.all([
          fetch(`${API}/analytics/stats`),
          fetch(`${API}/admin/messages`, { headers: { Authorization: `Bearer ${tk}` } }),
        ]);
        if (m.status === 401) {
          logout();
          return;
        }
        setStats(await s.json());
        setMessages(await m.json());
      } catch {
        setError("Impossible de charger les données. Réessayez.");
      }
    },
    [logout]
  );

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const exportCsv = async () => {
    try {
      const res = await fetch(`${API}/admin/messages/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "messages-beintec.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError("Export impossible. Réessayez.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(typeof data.detail === "string" ? data.detail : "Identifiants incorrects.");
      }
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-slate-300 bg-white px-4 py-3.5 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-all duration-300 focus:border-bei-brand focus:ring-4 focus:ring-bei-brand/10";

  if (!token) {
    return (
      <div data-testid="admin-login-page" className="relative flex min-h-screen items-center justify-center overflow-hidden bg-bei-navy px-4">
        <div className="grid-overlay absolute inset-0" />
        <div className="absolute -left-32 top-1/4 h-[380px] w-[380px] rounded-full bg-bei-brand/20 blur-[140px]" />
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="relative w-full max-w-md rounded-3xl border border-white/10 bg-white/95 p-8 shadow-2xl backdrop-blur-xl sm:p-10"
        >
          <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-bei-navy text-bei-cyan">
            <Lock className="h-5 w-5" />
          </span>
          <h1 className="mt-6 font-display text-2xl font-extrabold tracking-tight text-bei-navy sm:text-3xl">
            Espace administrateur
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            Accès réservé à l'équipe BEINTEC.
          </p>
          <form data-testid="admin-login-form" onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <label htmlFor="a-email" className="mb-2 block font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                Email
              </label>
              <input id="a-email" data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@beintec.com" className={inputCls} />
            </div>
            <div>
              <label htmlFor="a-pass" className="mb-2 block font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                Mot de passe
              </label>
              <input id="a-pass" data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={inputCls} />
            </div>
            {error && (
              <p data-testid="admin-login-error" className="rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-600">
                {error}
              </p>
            )}
            <button
              data-testid="admin-login-submit"
              type="submit"
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-bei-brand px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-bei-azure disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {loading ? "Connexion…" : "Se connecter"}
            </button>
          </form>
          <Link to="/" data-testid="admin-back-home" className="group mt-6 inline-flex items-center gap-2 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 transition-colors hover:text-bei-brand">
            <ArrowLeft className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-x-1" />
            Retour au site
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-bei-mist pb-20 pt-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-bei-cyan">Tableau de bord</p>
            <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight text-bei-navy">Vue d'ensemble</h1>
          </div>
          <div className="flex gap-3">
            <button
              data-testid="admin-refresh-button"
              onClick={() => load(token)}
              className="flex items-center gap-2 rounded-full border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 transition-all duration-300 hover:border-bei-brand hover:text-bei-brand"
            >
              <RefreshCw className="h-4 w-4" />
              Actualiser
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 rounded-full bg-bei-navy px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-bei-deep"
            >
              <LogOut className="h-4 w-4" />
              Déconnexion
            </button>
          </div>
        </div>

        <div className="mt-10 grid gap-6 sm:grid-cols-3">
          <div data-testid="admin-stat-visits" className="rounded-2xl border border-slate-200 bg-white p-7">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-bei-brand/10 text-bei-brand">
              <Eye className="h-5 w-5" />
            </span>
            <p className="mt-4 font-display text-4xl font-extrabold text-bei-navy">{stats ? stats.total : "—"}</p>
            <p className="mt-1 text-sm text-slate-500">Visites totales (visiteurs consentants)</p>
          </div>
          <div data-testid="admin-stat-messages" className="rounded-2xl border border-slate-200 bg-white p-7">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-bei-cyan/15 text-bei-brand">
              <Mail className="h-5 w-5" />
            </span>
            <p className="mt-4 font-display text-4xl font-extrabold text-bei-navy">{messages ? messages.length : "—"}</p>
            <p className="mt-1 text-sm text-slate-500">Messages reçus via le formulaire</p>
          </div>
          <div data-testid="admin-stat-pages" className="rounded-2xl border border-slate-200 bg-white p-7">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-bei-navy/10 text-bei-navy">
              <RefreshCw className="h-5 w-5" />
            </span>
            <p className="mt-4 font-display text-4xl font-extrabold text-bei-navy">{stats ? stats.by_path.length : "—"}</p>
            <p className="mt-1 text-sm text-slate-500">Pages visitées distinctes</p>
          </div>
        </div>

        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-7">
          <h2 className="font-display text-lg font-bold text-bei-navy">Visites par page</h2>
          <div className="mt-5 space-y-3">
            {stats && stats.by_path.length > 0 ? (
              stats.by_path.map((p) => {
                const max = stats.by_path[0].views || 1;
                return (
                  <div key={p.path} data-testid={`admin-path-${p.path.replace(/\W+/g, "-")}`} className="flex items-center gap-4">
                    <span className="w-56 truncate font-mono text-xs text-slate-600 sm:w-72">{p.path}</span>
                    <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                      <div className="h-full rounded-full bg-bei-brand transition-all duration-700" style={{ width: `${Math.max(6, (p.views / max) * 100)}%` }} />
                    </div>
                    <span className="w-10 text-right font-display text-sm font-bold text-bei-navy">{p.views}</span>
                  </div>
                );
              })
            ) : (
              <p data-testid="admin-no-visits" className="text-sm text-slate-500">Aucune visite enregistrée pour le moment.</p>
            )}
          </div>
        </div>

        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-7">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-display text-lg font-bold text-bei-navy">Messages du formulaire de contact</h2>
            <button
              data-testid="admin-export-csv-button"
              onClick={exportCsv}
              className="flex items-center gap-2 rounded-full bg-bei-brand px-5 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-bei-azure"
            >
              <Download className="h-4 w-4" />
              Exporter en CSV (Excel)
            </button>
          </div>
          <div className="mt-5 space-y-4">
            {messages && messages.length > 0 ? (
              messages.map((m, i) => (
                <div key={m.id || i} data-testid={`admin-message-${i}`} className="rounded-xl border border-slate-200 bg-bei-mist/60 p-5">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="font-display text-base font-bold text-bei-navy">
                      {m.name}
                      {m.company ? <span className="ml-2 font-sans text-sm font-normal text-slate-500">· {m.company}</span> : null}
                    </p>
                    <span className="font-mono text-[10px] uppercase tracking-wider text-slate-400">
                      {m.created_at ? new Date(m.created_at).toLocaleString("fr-FR") : ""}
                    </span>
                  </div>
                  <p className="mt-1 font-mono text-xs text-bei-brand">{m.subject}</p>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{m.message}</p>
                  <p className="mt-3 text-xs text-slate-500">
                    <a href={`mailto:${m.email}`} className="font-semibold text-bei-brand hover:underline">{m.email}</a>
                    {m.phone ? ` · ${m.phone}` : ""}
                  </p>
                </div>
              ))
            ) : (
              <p data-testid="admin-no-messages" className="text-sm text-slate-500">Aucun message reçu pour le moment.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admin;
